import React from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Modal, ModalTitle, ModalBody, ModalFooter } from 'design-system/src/components/Modal';
import Button from 'design-system/src/components/Button/Button';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { Toast, StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { client, ErrorResponse } from '../../api';
import { formatPhoneNumber } from 'shared-utils/utils/formatPhoneNumber';

interface RemovePhoneNumberModalProps {
  /** Callback to close the modal */
  onClose: () => void;
  /** Raw digits phone number to remove */
  phoneNumber: string;
}

/**
 * Confirmation modal for removing an office phone number.
 */
const RemovePhoneNumberModal: React.FC<RemovePhoneNumberModalProps> = ({ onClose, phoneNumber }) => {
  const queryClient = useQueryClient();

  const removePhoneNumber = useMutation<unknown, AxiosError<ErrorResponse>>({
    mutationFn: async () => {
      return client.delete(`settings/phone-numbers/${phoneNumber}`).then((res) => res.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['office-phone-numbers'] });
      toast.custom(() => <Toast variant="success">Phone number removed</Toast>);
      onClose();
    },
    onError: (error) => {
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
    },
  });

  return (
    <Modal onClose={onClose}>
      <ModalTitle>Remove office number?</ModalTitle>
      <ModalBody>
        <Typography>
          <strong>{formatPhoneNumber(phoneNumber)}</strong> will no longer be associated with your organization.
        </Typography>
      </ModalBody>
      <ModalFooter>
        <Button onClick={onClose} variant="tertiary" size="sm" disabled={removePhoneNumber.isPending}>
          Back
        </Button>
        <Button onClick={() => removePhoneNumber.mutate()} isLoading={removePhoneNumber.isPending} size="sm">
          Remove
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default RemovePhoneNumberModal;
